import React, { useEffect, useState } from "react";
import TextView from "../../../TextView";
import Input from "../../../Input";
import styled from "styled-components";
import { colors } from "../../../../utils";
import Dropdown from "../../../Dropdown";
import PaletteItem from "./PaletteItem";
import { colorMoods, colorSchemes, PALETTE_TYPES } from "./constants";
import { useDispatch, useSelector } from "react-redux";
import {
  getBaseColor,
  getMood,
  getPaletteColors,
  getScheme,
} from "../../../../redux/selectors/colorPalette";
import {
  addColorPalettes,
  setBaseColor,
  setColorMood,
  setColorScheme,
  updateColorPalette,
} from "../../../../redux/reducers/colorPalette";
import {
  getSegmentColorPalette,
  getSelectedSegment,
  getWheelColorPalette,
} from "../../../../redux/selectors/segmentBuilderSelectors";
import { generateScheme } from "../../../../services/api";
import { getRandomBrightColor, getRandomId } from "../../../../utils/helpers";
import CustomColorPicker from "../../../CustomColorPicker";
import SuggestionPrompt from "./SuggestionPrompt";
import { ASSETS } from "../../../../utils/config";
import { CircularProgress } from "@mui/material";
import Edit from "assets/images/newEdit.svg";
import { Palette } from "@material-ui/icons";
import Button from "../../../Button";

const Row = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 16px;
  align-items: center;
  margin-top: 10px;
  margin-bottom: 20px;
`;

const Circle = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: lightblue;
  border: 1px solid ${colors.lightBorder};
  cursor: pointer;
  position: relative;
`;

export default function SegmentColor({
  paletteType,
  handleSelect,
}) {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [segmentColor, setSegmentColor] = useState("");
  const [temporaryColor, setTemporaryColor] = useState("");
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [suggestedColor, setSuggestedColor] = useState(null);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const paletteColors = useSelector((state) =>
    getPaletteColors(state, "color", paletteType)
  );

  const baseColor = useSelector(getBaseColor)?.toUpperCase();
  const selectedMood = useSelector((state) => getMood(state, paletteType));
  const [isValidColor, setIsValidColor] = useState(true);
  const selectedScheme = useSelector((state) => getScheme(state, paletteType));
  const selectedArc = useSelector(getSelectedSegment) || {};
  const { settings: segmentSettings = {} } = selectedArc;
  const { color: initialSegmentColor } = segmentSettings;
  const segmentColorPalette = useSelector(getSegmentColorPalette);
  const wheelColorPalette = useSelector(getWheelColorPalette);

  const transformPaletteData = (data, color, previousPalette) => {
    return data.map((item, i) => ({
      index: i,
      paletteName: item.palette_name,
      color,
      colors: item.colors.map((color, ind) => ({
        hex: color.hex.value,
        id: getRandomId(),
        selected: false,
        locked:
          i == previousPalette?.index
            ? previousPalette?.colors[ind]?.locked
            : false || (paletteType == PALETTE_TYPES.SEGMENT && ind === 0), // Lock the first color which is the segment color
        index: ind,
      })),
      id: getRandomId(),
      selected: i === 0,
    }));
  };

  useEffect(() => {
    if (paletteType == PALETTE_TYPES.SEGMENT) {
      if (segmentColorPalette) {
        dispatch(
          addColorPalettes({
            paletteName:segmentColorPalette.palette_name,
            paletteType,
            paletteColors:[{...segmentColorPalette,selected:true}],
            type:"color",
          })
        );
      }
    } else {
      if (wheelColorPalette) {
        dispatch(
          addColorPalettes({
            paletteName:wheelColorPalette.paletteName,
            paletteType,
            type: "color",
            paletteColors: [{...wheelColorPalette,selected:true}],
            selected:true
          })
        );
        setTemporaryColor(wheelColorPalette.colors[0].hex);
        dispatch(setBaseColor({ color: wheelColorPalette.colors[0].hex }));
      }
    }
    if(!wheelColorPalette && !segmentColorPalette) handleGenerateSchemes()
  }, [segmentColorPalette, wheelColorPalette]);

  useEffect(() => {
    if (paletteType === PALETTE_TYPES.SEGMENT) {
      if (!segmentColor) {
        setSegmentColor(initialSegmentColor.toUpperCase());
        setTemporaryColor(initialSegmentColor);
      }
    } else {
      setTemporaryColor(baseColor);
    }
  }, [initialSegmentColor, segmentColor, baseColor]);

  // useEffect(() => {
  //   handleGenerateSchemes();
  // }, [selectedMood, selectedScheme]);

  const handleGenerateSchemes = async (palette, color) => {
   
    const currentColor = color ? color :
      paletteType === PALETTE_TYPES.WHEEL ? baseColor : segmentColor;

      const baseHexes = paletteType === PALETTE_TYPES.WHEEL
      ? [color || baseColor, ...Array(19).fill(null)]
      : [color || segmentColor, null, null, null, null]

    if (!currentColor && !palette) return;

    try {
      setShowSuggestion(false);
      const hexes = currentColor!==palette?.colors[0].hex ? baseHexes : palette
        ? palette.colors.map((color, ind) =>
            color.locked || ind == 0 ? color.hex : null
          )
        : baseHexes

      const shouldPaletteUpdate = palette
        ? palette.colors.some((color) => color.locked)
        : false;

      setLoading(true);
      const response = await generateScheme({
        hex: hexes,
        mode: "auto",
        count: paletteType == PALETTE_TYPES.WHEEL ? 20 : 5,
        scheme_count: 1,
        mood: "auto",
      });

      if (response.data.success) {
        setShowSuggestion(false);
        // setShowSuggestionIcon(false);
        const finalData = transformPaletteData(
          response.data.data,
          currentColor,
          palette
        );

        if (shouldPaletteUpdate) {
          dispatch(
            updateColorPalette({
              paletteType,
              type: "color",
              paletteId: palette.id,
              updatedPalette: finalData[0],
            })
          );
        } else {
          dispatch(
            addColorPalettes({
              paletteType,
              type: "color",
              paletteColors: finalData,
            })
          );
        }
      } else {
        setSuggestedColor(response.data.data);
        setShowSuggestion(true);
        // setShowSuggestionIcon(true);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(
        "An error occurred while generating schemes:",
        error.message
      );
    }
  };

  const handleMoodChange = (e) => {
    dispatch(setColorMood({ paletteType, mood: e.target.value }));
  };

  const handleSchemeChange = (e) => {
    dispatch(setColorScheme({ paletteType, scheme: e.target.value }));
  };

  const handleColorChange = (color) => {
    setTemporaryColor(color); // Update the temporary color in the picker
  };

  const handleColorInputChange = (e) => {
    const inputValue = e.target.value.toUpperCase();
    const hexValue = inputValue.replace(/[^A-F0-9]/gi, "").slice(0, 6);
    const newColor = `#${hexValue}`;

    setIsValidColor(/^#[0-9A-F]{6}$/i.test(newColor) || newColor === "#");

    if (paletteType === PALETTE_TYPES.WHEEL) {
      dispatch(setBaseColor({ color: newColor }));
    } else {
      setSegmentColor(newColor);
    }
  };

  const handleConfirmColor = () => {
    if (paletteType === PALETTE_TYPES.WHEEL) {
      dispatch(setBaseColor({ color: temporaryColor }));
    } else {
      setSegmentColor(temporaryColor);
    }
    setShowColorPicker(false);
    handleGenerateSchemes(undefined, temporaryColor);
  };

  const handleCloseSuggestion = () => {
    setShowSuggestion(false);
    // setShowSuggestionIcon(false);
  };

  const applySuggestedColor = () => {
    setSegmentColor(suggestedColor?.adjusted);
    handleGenerateSchemes(undefined, suggestedColor.adjusted);
    setShowSuggestion(false);
  };

  const handleIgnoreSuggestion = () => {
    setShowSuggestion(false);
    handleGenerateSchemes();
  };

  return (
    <div>
      <TextView>
        {paletteType == "wheel"
          ? "Select a color to generate a theme for your wheel!"
          : "Segment Color"}
      </TextView>
      {showSuggestion ? (
        <SuggestionPrompt
          suggestedColor={suggestedColor}
          onClose={handleCloseSuggestion}
          handleIgnoreSuggestion={handleIgnoreSuggestion}
          applySuggestedColor={applySuggestedColor}
        />
      ) : (
        <div>
          <Row>
            <Circle
              style={{
                backgroundColor:
                  paletteType === PALETTE_TYPES.WHEEL
                    ? baseColor
                    : segmentColor,
              }}
              onClick={() => setShowColorPicker(true)}
            >
              <img
                src={Edit}
                style={{ position: "absolute", bottom: 0, right: 0 }}
              />
            </Circle>

            <Input
              variant={"secondary"}
              containerStyle={{ marginBottom: 0, width: 160 }}
              inputStyle={{
                textAlign: "center",
                borderColor: isValidColor ? colors.lightGrey : "red",
              }}
              value={
                paletteType === PALETTE_TYPES.WHEEL ? baseColor : segmentColor
              }
              onChange={handleColorInputChange}
            />
            {/* {showSuggestionIcon && (
              <img
                style={{ height: 28, cursor: "pointer" }}
                src={ASSETS.SuggestionIcon}
                onClick={() => setShowSuggestion(true)}
              />
            )} */}
          </Row>
          {/* <Row style={{ gridTemplateColumns: "1fr auto" }}>
            <TextView>
              Combine Moods and Schemes for a Tailored Palette:
            </TextView>
            <TextView
              style={{ cursor: "pointer" }}
              color={colors.PrimaryPurple}
              onClick={() => handleGenerateSchemes()}
            >
              Regenerate All
            </TextView>
          </Row> */}
          {/* <Row style={{ gridTemplateColumns: "1fr 1fr" }}>
            <Dropdown
              variant="outlined"
              label={"Mood"}
              labelStyle={{ fontWeight: 500 }}
              options={colorMoods}
              value={selectedMood}
              onChange={handleMoodChange}
            />
            <Dropdown
              variant="outlined"
              label={"Scheme"}
              labelStyle={{ fontWeight: 500 }}
              options={colorSchemes}
              value={selectedScheme}
              onChange={handleSchemeChange}
            />
          </Row> */}
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "20%",
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <>
              {paletteColors.map((palette, i) => {
                return (
                  <PaletteItem
                    palette={palette}
                    index={i}
                    handleGenerateSchemes={handleGenerateSchemes}
                    type={"color"}
                    paletteType={paletteType}
                  />
                );
              })}
            </>
          )}
        </div>
      )}
      {showColorPicker && (
        <CustomColorPicker
          customStyle={{
            left: "45vw",
            bottom: "12vh",
          }}
          visible={showColorPicker}
          onClose={() => setShowColorPicker(false)}
          onConfirm={handleConfirmColor}
          color={temporaryColor}
          onColorChange={handleColorChange}
        />
      )}
    </div>
  );
}
